import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppShell } from "../components/layout/AppShell";
import { DataTable, type Column } from "../components/ui/DataTable";
import { Badge } from "../components/ui/Primitives";
import { Button } from "../components/ui/Button";
import { ConfirmDialog } from "../components/ui/Modal";
import { usePagedList } from "../hooks/usePagedList";
import { apiRequest, ApiError } from "../api/client";
import { useToast } from "../components/ui/Toast";
import type { Report } from "../api/types";

const statusTone = { pending: "warn", reviewed: "success", dismissed: "neutral" } as const;

export function ReportsPage() {
  const { rows, isLoading, error, page, setPage, hasNextPage, reload } = usePagedList<Report>("/admin/reports");
  const [busyId, setBusyId] = useState<number | null>(null);
  const [pendingDeletePost, setPendingDeletePost] = useState<Report | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();
  const navigate = useNavigate();

  async function resolve(report: Report, status: "reviewed" | "dismissed") {
    setBusyId(report.id);
    try {
      await apiRequest(`/admin/reports/${report.id}/resolve`, { method: "POST", body: { status } });
      showToast(`Report marked ${status}`, "success");
      reload();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not update report", "error");
    } finally {
      setBusyId(null);
    }
  }

  async function confirmDeletePost() {
    if (!pendingDeletePost) return;
    setIsSubmitting(true);
    try {
      await apiRequest(`/admin/posts/${pendingDeletePost.target_id}`, { method: "DELETE" });
      await apiRequest(`/admin/reports/${pendingDeletePost.id}/resolve`, { method: "POST", body: { status: "reviewed" } });
      showToast("Post deleted and report marked reviewed", "success");
      reload();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not delete post", "error");
    } finally {
      setIsSubmitting(false);
      setPendingDeletePost(null);
    }
  }

  const columns: Column<Report>[] = [
    {
      header: "Target",
      render: (row) =>
        row.target_type === "user" ? (
          <button className="font-medium text-gold hover:underline" onClick={() => navigate(`/users/${row.target_id}`)}>
            user #{row.target_id.slice(0, 8)}...
          </button>
        ) : (
          <span className="font-medium">
            {row.target_type} #{row.target_id}
          </span>
        ),
    },
    { header: "Reason", render: (row) => <span className="max-w-xs truncate">{row.reason}</span> },
    { header: "Status", render: (row) => <Badge tone={statusTone[row.status]}>{row.status}</Badge> },
    { header: "Filed", render: (row) => new Date(row.created_at).toLocaleString() },
    {
      header: "",
      className: "text-right",
      render: (row) =>
        row.status === "pending" ? (
          <div className="flex justify-end gap-2">
            {row.target_type === "post" && (
              <Button size="sm" variant="danger" onClick={() => setPendingDeletePost(row)}>
                Delete Post
              </Button>
            )}
            {row.target_type === "user" && (
              <Button size="sm" variant="secondary" onClick={() => navigate(`/users/${row.target_id}`)}>
                Review User
              </Button>
            )}
            <Button size="sm" variant="secondary" disabled={busyId === row.id} onClick={() => resolve(row, "dismissed")}>
              Dismiss
            </Button>
            <Button size="sm" variant="primary" disabled={busyId === row.id} onClick={() => resolve(row, "reviewed")}>
              Mark Reviewed
            </Button>
          </div>
        ) : null,
    },
  ];

  return (
    <AppShell title="Reports">
      <p className="mb-4 text-xs text-ink-faint">
        Covers reports against users, posts, rooms, and chat messages. There's no separate "Comments" moderation
        here because commenting doesn't exist as a feature anywhere in the app yet — Moments/Posts support captions
        only, not threaded comments. If that feature is built, its reports would flow through this same queue.
      </p>

      <DataTable
        columns={columns}
        rows={rows}
        keyField={(r) => r.id}
        isLoading={isLoading}
        error={error}
        onRetry={reload}
        emptyTitle="No reports"
        pagination={{ page, pageSize: 20, hasNextPage, onPageChange: setPage }}
      />

      <ConfirmDialog
        isOpen={!!pendingDeletePost}
        onClose={() => setPendingDeletePost(null)}
        onConfirm={confirmDeletePost}
        isLoading={isSubmitting}
        isDangerous
        title="Delete the reported post?"
        description="This permanently removes the post and marks this report as reviewed. This cannot be undone."
        confirmLabel="Delete Post"
      />
    </AppShell>
  );
}
